import React, { useState } from 'react';
import axios from 'helpers/axios';
import config from 'helpers/config';
import FeeType from 'models/types/FeeType';

interface Props {
    serviceItemId: string;
    feeName: string;
    initialType: FeeType;
    initialAmount?: number;
    onClose: (updated: boolean) => void;
}

// Always US formatting: dot decimal, comma thousands, two decimal places.
// (A raw type="number" input follows the machine locale, so it can render a
// comma decimal and drops trailing zeros — hence the explicit en-US format.)
const formatAmount = (raw: string): string => {
    const cleaned = String(raw).replace(/,/g, '').trim();
    if (cleaned === '') return '';
    const n = Number(cleaned);
    if (isNaN(n)) return '';
    return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const FeeOverrideModal: React.FC<Props> = ({ serviceItemId, feeName, initialType, initialAmount, onClose }) => {
    const [amount, setAmount] = useState<string>(initialAmount != null ? formatAmount(String(initialAmount)) : '');
    const [type, setType] = useState<FeeType>(initialType || FeeType.Fixed);
    const [isSaving, setIsSaving] = useState(false);

    const save = () => {
        setIsSaving(true);
        axios
            .put(`${config.apiUrl}/serviceItem/${serviceItemId}/feeOverride`, {
                overrideFeeType: type,
                overrideFeeAmount: Number(String(amount).replace(/,/g, '')),
            })
            .then(() => onClose(true))
            .catch(() => setIsSaving(false));
    };

    const toggleClass = (active: boolean) =>
        `px-4 py-2 font-semibold clickable ${
            active ? 'bg-blue-500 text-white' : 'bg-white text-blue-500 hover:bg-blue-50'
        }`;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
            <div className="bg-white rounded-xl shadow-xl p-5 w-96" onClick={(e) => e.stopPropagation()}>
                <h3 className="font-semibold text-gray-800 mb-4">{feeName}</h3>

                <div className="flex items-center mb-6">
                    <input
                        autoFocus
                        type="text"
                        inputMode="decimal"
                        value={amount}
                        onChange={(e) => setAmount(e.currentTarget.value.replace(/[^0-9.,]/g, ''))}
                        onBlur={() => setAmount(formatAmount(amount))}
                        className="flex-1 border border-blue-300 rounded-md px-3 py-2 mr-2 bg-blue-50 font-semibold focus:outline-none focus:ring-1 focus:border-blue-500"
                    />
                    <div className="flex rounded-md border border-gray-300 overflow-hidden">
                        <button
                            className={toggleClass(type === FeeType.Percentage)}
                            onClick={() => setType(FeeType.Percentage)}
                        >
                            %
                        </button>
                        <button className={toggleClass(type === FeeType.Fixed)} onClick={() => setType(FeeType.Fixed)}>
                            $
                        </button>
                    </div>
                </div>

                <div className="flex">
                    <button
                        className="flex-1 mr-2 py-2 rounded-md text-blue-500 font-semibold clickable hover:bg-blue-50"
                        onClick={() => onClose(false)}
                    >
                        Cancel
                    </button>
                    <button
                        disabled={isSaving}
                        className="flex-1 py-2 rounded-md bg-blue-600 text-white font-semibold clickable hover:bg-blue-700 disabled:opacity-50"
                        onClick={save}
                    >
                        Save
                    </button>
                </div>
            </div>
        </div>
    );
};

export default FeeOverrideModal;
